import { createSlice } from "@reduxjs/toolkit";

// Decode JWT payload to extract type/role and userId without a library
const decodeJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const DEFAULT_PERMISSIONS = { canOnrampOfframp: false, canOnramp: false, canOfframp: false };

const loadPermissionsFromSession = () => {
  try {
    const raw = sessionStorage.getItem("brnx_quote_permissions");
    if (!raw) return DEFAULT_PERMISSIONS;
    return { ...DEFAULT_PERMISSIONS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PERMISSIONS;
  }
};

const loadFromSession = () => {
  try {
    const accessToken = sessionStorage.getItem("brnx_access_token");
    const refreshToken = sessionStorage.getItem("brnx_refresh_token");
    if (!accessToken) return null;
    const decoded = decodeJwt(accessToken);
    return {
      accessToken,
      refreshToken,
      role: decoded?.data?.type || null,    // "sender" | "receiver"
      userId: decoded?.data?.userId || null,
      country: sessionStorage.getItem("brnx_country") || null,
      email: sessionStorage.getItem("brnx_email") || decoded?.data?.email || null,
      userName: sessionStorage.getItem("brnx_user_name") || null,
      quotePermissions: loadPermissionsFromSession(),
      status: sessionStorage.getItem("brnx_status") || null,
    };
  } catch {
    return null;
  }
};

const stored = loadFromSession();

const authSlice = createSlice({
  name: "auth",
  initialState: {
    accessToken: stored?.accessToken || null,
    refreshToken: stored?.refreshToken || null,
    role: stored?.role || null,
    userId: stored?.userId || null,
    country: stored?.country || null,
    email: stored?.email || null,
    userName: stored?.userName || null,
    quotePermissions: stored?.quotePermissions || DEFAULT_PERMISSIONS,
    status: stored?.status || null,
    kybPrefill: null,
  },
  reducers: {
    setCredentials(state, action) {
      const { accessToken, refreshToken } = action.payload;
      const decoded = decodeJwt(accessToken);
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.role = decoded?.data?.type || null;
      state.userId = decoded?.data?.userId || null;
      try {
        sessionStorage.setItem("brnx_access_token", accessToken);
        sessionStorage.setItem("brnx_refresh_token", refreshToken);
      } catch {}
    },
    setQuotePermissions(state, action) {
      state.quotePermissions = { ...DEFAULT_PERMISSIONS, ...action.payload };
      try {
        sessionStorage.setItem("brnx_quote_permissions", JSON.stringify(state.quotePermissions));
      } catch {}
    },
    setUserEmail(state, action) {
      state.email = action.payload;
      try {
        sessionStorage.setItem("brnx_email", action.payload || "");
      } catch { }
    },
    setUserName(state, action) {
      state.userName = action.payload;
      try {
        sessionStorage.setItem("brnx_user_name", action.payload || "");
      } catch { }
    },
    clearCredentials(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.role = null;
      state.userId = null;
      state.country = null;
      state.email = null;
      state.userName = null;
      state.quotePermissions = DEFAULT_PERMISSIONS;
      state.status = null;
      state.kybPrefill = null;
      try {
        sessionStorage.clear();
      } catch { }
    },
    setKybPrefill(state, action) {
      state.kybPrefill = action.payload;
    },
    setUserCountry(state, action) {
      state.country = action.payload;
      try {
        sessionStorage.setItem("brnx_country", action.payload || "");
      } catch { }
    },
    setUserStatus(state, action) {
      state.status = action.payload;
      try {
        sessionStorage.setItem("brnx_status", action.payload || "");
      } catch { }
    },
  },
});

export const {
  setCredentials,
  setQuotePermissions,
  clearCredentials,
  setKybPrefill,
  setUserCountry,
  setUserEmail,
  setUserName,
  setUserStatus,
} = authSlice.actions;
export default authSlice.reducer;
